import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Trophy, Award, LogIn, Download, Swords, ChevronRight, Check } from "lucide-react";
import { CelebPortrait } from "../components/CelebPortrait";
import { RoundTimer, DangerFlash } from "../components/RoundTimer";
import { StartButton } from "../components/StartButton";
import { TrophyRoomModal } from "../components/TrophyRoomModal";
import { useAuth } from "../components/AuthProvider";
import { playClick, playSelect, playAffirm, playReveal, playError } from "../lib/sounds";
import { setTimeLimit } from "../lib/timerSettings";

const API = process.env.REACT_APP_BACKEND_URL;

const ROUNDS = 7;
const SECS_PER_ROUND = 30;

export default function DailySmash() {
  const nav = useNavigate();
  const { user, login } = useAuth();
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [stage, setStage] = useState("start");        // start | round | reveal
  const [roundIdx, setRoundIdx] = useState(0);
  const [roundStage, setRoundStage] = useState("choose"); // choose | reason
  const [answers, setAnswers] = useState([]);         // [{chosen_side, chosen_reason} × 7]
  const [danger, setDanger] = useState(false);
  const [reveal, setReveal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Trophy Room — populated only when the user arrived via a Challenge-a-Friend link.
  const [vsCode, setVsCode] = useState(null);
  const [trophyOpen, setTrophyOpen] = useState(false);
  useEffect(() => {
    try {
      const c = sessionStorage.getItem("cs.vsChallengeCode");
      if (c) setVsCode(c);
    } catch {}
  }, []);

  // Pin the round timer to 30s while the daily is on screen; restore afterwards.
  useEffect(() => {
    try {
      const prev = localStorage.getItem("cs.timeLimit");
      setTimeLimit(SECS_PER_ROUND);
      return () => {
        if (prev !== null) setTimeLimit(Number(prev) || 0);
      };
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/daily/today`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => {
        if (!alive) return;
        setToday(d);
        setAnswers(new Array(ROUNDS).fill(null).map(() => ({
          chosen_side: null, chosen_reason: null,
        })));
        if (d.already_played && d.me?.last_submission) {
          const last = d.me.last_submission;
          const legacy = !Array.isArray(last.rounds) || last.rounds.length === 0;
          setReveal({
            daily_id: d.daily_id, smash_number: d.smash_number, factor: d.factor,
            rounds: last.rounds || [],
            legacy_submission: legacy,
            score: { breakdown: last.score_breakdown, total: last.score_total,
                     correct_celebs: last.correct_celebs || 0,
                     perfect_rounds: last.perfect_rounds || 0,
                     is_perfect_game: last.perfect_rounds === ROUNDS,
                     rounds_total: ROUNDS },
            archetype: last.archetype,
            me: { css: d.me.css, css_delta: last.css_delta,
                  daily_streak: d.me.daily_streak, longest_streak: d.me.longest_streak,
                  title: d.me.title, global_rank: d.me.global_rank },
          });
          setStage("reveal");
        }
      })
      .catch(() => alive && setErr("Couldn't load today's Smash."))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const factor = today?.factor;
  const pairings = today?.pairings || [];
  const reasons = today?.reasons || [];

  const current = useMemo(() => ({
    pairing: pairings[roundIdx],
    answer: answers[roundIdx] || { chosen_side: null, chosen_reason: null },
  }), [pairings, answers, roundIdx]);

  const setAnswer = (patch) => {
    setAnswers((prev) => {
      const next = prev.slice();
      next[roundIdx] = { ...next[roundIdx], ...patch };
      return next;
    });
  };

  const submitAll = useCallback(async (all) => {
    if (submitting) return;
    playAffirm();
    setSubmitting(true);
    try {
      const rounds = all.map((a, i) => ({
        pairing_id: pairings[i].id,
        chosen_side: a.chosen_side || "a",
        chosen_reason: a.chosen_reason || reasons[reasons.length - 1], // Pure Instinct fallback
      }));
      const r = await fetch(`${API}/api/daily/submit`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          daily_id: today.daily_id, factor_id: factor.id, rounds,
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || "Submit failed");
      }
      const data = await r.json();
      setReveal(data);
      setStage("reveal");
      setTimeout(() => playReveal(), 250);
      // Trophy Room — only if the user came via a Challenge-a-Friend link.
      if (vsCode) {
        setTimeout(() => setTrophyOpen(true), 1600);
      }
    } catch (e) {
      playError();
      toast.error(e.message || "Couldn't submit");
    } finally {
      setSubmitting(false);
    }
  }, [submitting, today, factor, pairings, reasons]);

  const nextRound = useCallback(() => {
    playAffirm();
    if (roundIdx + 1 < ROUNDS) {
      setRoundIdx((i) => i + 1);
      setRoundStage("choose");
    } else {
      submitAll(answers);
    }
  }, [roundIdx, answers, submitAll]);

  const onRoundStageNext = () => {
    if (roundStage === "choose") {
      if (!current.answer.chosen_side) { playError(); return; }
      playAffirm();
      setRoundStage("reason");
    } else {
      if (!current.answer.chosen_reason) { playError(); return; }
      nextRound();
    }
  };

  const onTimeout = useCallback(() => {
    // Auto-advance: if choose stage empty, pick "a"; if reason empty, pick Pure Instinct.
    setAnswers((prev) => {
      const next = prev.slice();
      const cur = next[roundIdx] || {};
      if (!cur.chosen_side) cur.chosen_side = "a";
      if (!cur.chosen_reason) cur.chosen_reason = reasons[reasons.length - 1];
      next[roundIdx] = cur;
      if (roundIdx + 1 < ROUNDS) {
        setRoundIdx(roundIdx + 1);
        setRoundStage("choose");
      } else {
        // Submit with whatever we have
        setTimeout(() => submitAll(next), 30);
      }
      return next;
    });
  }, [roundIdx, reasons, submitAll]);

  if (loading) return <FullScreenMsg text="Loading today's Smash…" />;
  if (err) return <FullScreenMsg text={err} />;
  if (!today) return null;

  return (
    <div className="min-h-screen spotlight grain scanlines">
      <div className="max-w-md mx-auto px-5 pt-16 pb-nav">
        <Header today={today} user={user} onLogin={login} roundIdx={roundIdx} stage={stage} />

        {/* One shared 30s timer per round — resets on round change */}
        {stage === "round" && (
          <div className="mt-4">
            <RoundTimer
              resetKey={`r-${roundIdx}`}
              onDangerChange={setDanger}
              onExpire={onTimeout}
            />
          </div>
        )}
        <DangerFlash active={danger && stage === "round"} />

        <AnimatePresence mode="wait">
          {stage === "start" && (
            <StageStart key="start" factor={factor}
              pairings={pairings}
              onStart={() => { setStage("round"); setRoundIdx(0); setRoundStage("choose"); }}
            />
          )}
          {stage === "round" && current.pairing && (
            <StageRound
              key={`round-${roundIdx}-${roundStage}`}
              factor={factor}
              pairing={current.pairing}
              roundStage={roundStage}
              answer={current.answer}
              onPick={(side) => { playSelect(); setAnswer({ chosen_side: side }); }}
              onReason={(r) => { playSelect(); setAnswer({ chosen_reason: r }); }}
              onNext={onRoundStageNext}
              reasons={reasons}
              submitting={submitting}
              isLast={roundIdx === ROUNDS - 1}
            />
          )}
          {stage === "reveal" && reveal && (
            <StageReveal
              key="reveal" reveal={reveal} pairings={pairings} user={user}
              onLogin={login} onLeaderboard={() => nav("/leaderboards/daily")}
            />
          )}
        </AnimatePresence>
      </div>
      {trophyOpen && vsCode && (
        <TrophyRoomModal
          code={vsCode}
          currentUserId={user?.user_id}
          onClose={() => {
            setTrophyOpen(false);
            try { sessionStorage.removeItem("cs.vsChallengeCode"); } catch {}
          }}
        />
      )}
    </div>
  );
}

/* ---------- Header ---------- */
const Header = ({ today, user, onLogin, roundIdx, stage }) => {
  const { factor, smash_number, me } = today;
  return (
    <div data-testid="daily-header">
      <div className="flex items-baseline justify-between">
        <span className="text-[10px] tracking-[0.35em] uppercase font-bold text-[#FF107A]">
          Daily Smash #{smash_number}
        </span>
        <button
          data-testid="header-signin-btn"
          onClick={user ? undefined : onLogin}
          className="text-[10px] tracking-[0.2em] uppercase text-[#FDFBF7]/60 hover:text-[#FF107A]"
        >
          {user ? (user.name?.split(" ")[0] || "Signed in") : "Sign in"}
        </button>
      </div>
      <div className="mt-1 text-[9px] tracking-[0.3em] uppercase text-[#00E5FF] font-bold">
        Smash Factor {stage === "round" ? ` · Round ${roundIdx + 1} / ${ROUNDS}` : ""}
      </div>
      <h1 data-testid="daily-factor"
        className="font-display text-[28px] leading-[1] tracking-tight mt-1">
        {factor.label}
      </h1>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <HeaderStat label="CSS" value={me ? fmt(me.css) : "—"} accent="#FFD700" testId="head-css" />
        <HeaderStat label="Streak" value={me ? me.daily_streak || 0 : "—"} accent="#FF107A" testId="head-streak" />
        <HeaderStat label="Rank" value={me?.global_rank ? `#${me.global_rank}` : "—"} accent="#00E5FF" testId="head-rank" />
      </div>
    </div>
  );
};
const HeaderStat = ({ label, value, accent, testId }) => (
  <div className="rounded-xl border border-white/10 bg-black/40 px-2 py-2 text-center">
    <div className="font-display text-xl leading-none tabular" style={{ color: accent }} data-testid={testId}>
      {value}
    </div>
    <div className="text-[9px] tracking-[0.2em] uppercase text-[#FDFBF7]/60 mt-1">{label}</div>
  </div>
);
const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : (n ?? "0"));

/* ---------- Stage: Start ---------- */
const StageStart = ({ factor, pairings, onStart }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
    transition={{ duration: 0.25 }} className="mt-10 text-center"
    data-testid="stage-start"
  >
    <div className="text-[10px] tracking-[0.35em] uppercase font-bold text-[#FFD700]">Today's Smash</div>
    <h2 className="font-display text-3xl leading-[0.95] tracking-tight mt-2 px-2">{factor.label}</h2>
    <div className="mt-6 grid grid-cols-2 gap-2 text-[10px] tracking-[0.2em] uppercase text-[#FDFBF7]/60">
      <div className="rounded-xl border border-white/10 bg-black/30 py-2">
        <div className="font-display text-2xl text-[#00E5FF] tabular">{ROUNDS}</div>
        Rounds
      </div>
      <div className="rounded-xl border border-white/10 bg-black/30 py-2">
        <div className="font-display text-2xl text-[#FF107A] tabular">{SECS_PER_ROUND}s</div>
        Per Round
      </div>
    </div>
    <div className="mt-5 text-[11px] text-[#FDFBF7]/50">
      Pick your side · Pick your reason · Beat the world
    </div>
    <StartButton onClick={onStart} testId="start-smash-btn" label="Start Smash" />
  </motion.div>
);

/* ---------- Stage: One round (choose or reason) ---------- */
const StageRound = ({ factor, pairing, roundStage, answer, onPick, onReason, onNext, reasons, submitting, isLast }) => {
  if (roundStage === "choose") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.22 }} className="mt-6"
        data-testid="stage-choose"
      >
        <div className="text-[10px] tracking-[0.35em] uppercase font-bold text-[#FF107A] text-center">
          Pick your side
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <button data-testid="choose-a" onClick={() => onPick("a")} className="flex flex-col items-center">
            <CelebPortrait name={pairing.a} size={148}
              selected={answer.chosen_side === "a"}
              dim={answer.chosen_side === "b"} testId="portrait-a" />
          </button>
          <button data-testid="choose-b" onClick={() => onPick("b")} className="flex flex-col items-center">
            <CelebPortrait name={pairing.b} size={148}
              selected={answer.chosen_side === "b"}
              dim={answer.chosen_side === "a"} testId="portrait-b" />
          </button>
        </div>
        <PrimaryBtn disabled={!answer.chosen_side} onClick={onNext} testId="stage-next-btn">
          Lock pick <ChevronRight size={16} />
        </PrimaryBtn>
      </motion.div>
    );
  }

  // reason stage
  const chosenName = answer.chosen_side === "a" ? pairing.a : pairing.b;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.22 }} className="mt-6"
      data-testid="stage-reason"
    >
      <div className="text-[10px] tracking-[0.35em] uppercase font-bold text-[#00E5FF] text-center">Why?</div>
      <div className="mt-2 font-display text-2xl text-center leading-none truncate">{chosenName}</div>
      <div className="mt-4 grid grid-cols-2 gap-2" data-testid="reasons-grid">
        {reasons.map((r) => {
          const active = answer.chosen_reason === r;
          return (
            <button key={r} data-testid={`reason-${slug(r)}`} onClick={() => onReason(r)}
              className={`rounded-xl border px-3 py-3 text-sm font-semibold text-left leading-tight transition-all ${
                active
                  ? "border-[#FF107A] bg-[#FF107A]/15 text-[#FDFBF7] shadow-[0_0_16px_rgba(255,16,122,0.35)]"
                  : "border-white/10 bg-black/40 text-[#FDFBF7]/80 hover:border-white/25"
              }`}
            >
              {r}
            </button>
          );
        })}
      </div>
      <PrimaryBtn disabled={!answer.chosen_reason || submitting} onClick={onNext} testId="reason-next-btn">
        {isLast ? (submitting ? "Revealing…" : "Reveal Results") : "Next Round"} <ChevronRight size={16} />
      </PrimaryBtn>
    </motion.div>
  );
};
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

/* ---------- Stage: Reveal ---------- */
const StageReveal = ({ reveal, pairings, user, onLogin, onLeaderboard }) => {
  const { factor, rounds, score, archetype, me, smash_number } = reveal;
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState(null);

  const share = async () => {
    if (!cardRef.current || busy) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2.5, backgroundColor: "#0A090D" });
      const link = document.createElement("a");
      link.download = `smash-${smash_number}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Saved to downloads");
    } catch { toast.error("Couldn't create image"); }
    finally { setBusy(false); }
  };

  const challengeFriend = async () => {
    if (!user) { onLogin(); return; }
    try {
      const r = await fetch(`${API}/api/daily/challenge`, { method: "POST", credentials: "include" });
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      const d = await r.json();
      // Use the OG-friendly share endpoint so iMessage / WhatsApp scrape meta tags.
      const url = `${window.location.origin}/api/share/daily/${d.code}`;
      setChallenge({ code: d.code, url });
      try { await navigator.clipboard.writeText(url); toast.success("Challenge link copied!"); }
      catch { toast.success("Challenge ready"); }
    } catch (e) { toast.error(e.message); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }} data-testid="stage-reveal" className="mt-8"
    >
      {/* Aggregate score card */}
      <div className="rounded-3xl border border-white/10 p-5"
        style={{ background: "radial-gradient(circle at 20% 10%, rgba(255,215,0,0.28), transparent 60%), #16141D" }}>
        <div className="text-[10px] tracking-[0.35em] uppercase font-bold text-[#FFD700]">Final Score</div>
        <div className="mt-1 font-display text-[64px] leading-[0.9] tabular text-[#FFD700]" data-testid="reveal-total">
          {score.total}
        </div>
        <div className="mt-2 text-sm text-[#FDFBF7]/80">
          {score.correct_celebs}/{score.rounds_total} celebs matched · {score.perfect_rounds} perfect
          {score.is_perfect_game && <span className="ml-2 text-[#FF107A] font-bold">FULL SWEEP</span>}
        </div>
      </div>

      {/* Newly-unlocked streak rewards */}
      {me?.newly_unlocked_rewards?.length > 0 && (
        <div
          data-testid="reveal-new-rewards"
          className="mt-4 rounded-2xl border-2 border-[#22C55E] bg-[#22C55E]/10 p-4"
        >
          <div className="text-[9px] tracking-[0.3em] uppercase font-bold text-[#22C55E]">
            🔓 New Rewards Unlocked
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {me.newly_unlocked_rewards.map((r) => (
              <div
                key={r.id}
                data-testid={`reward-${r.id}`}
                className="rounded-full px-3 py-1 text-[10px] tracking-[0.15em] uppercase font-bold border"
                style={{
                  color: r.color, borderColor: `${r.color}66`,
                  background: `${r.color}22`,
                  boxShadow: `0 0 12px ${r.color}55`,
                }}
              >
                {r.name} · {r.kind}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Archetype ribbon */}
      <div className="mt-4 rounded-2xl border-2 px-4 py-3 flex items-center gap-3"
        style={{ borderColor: "#FFD700", background: "rgba(255,215,0,0.08)" }}
        data-testid="reveal-archetype">
        <Award className="text-[#FFD700]" size={24} />
        <div>
          <div className="text-[9px] tracking-[0.3em] uppercase font-bold text-[#FFD700]">You are</div>
          <div className="font-display text-2xl leading-none">{archetype.label}</div>
          <div className="text-xs text-[#FDFBF7]/70">{archetype.tagline}</div>
        </div>
      </div>

      {/* Per-round breakdown */}
      {reveal.legacy_submission ? (
        <div className="mt-4 rounded-2xl border border-white/10 bg-black/40 p-4 text-sm text-[#FDFBF7]/70"
          data-testid="reveal-legacy-notice">
          Your previous smash was played on the older 1-round format. Come back tomorrow for the new 7-round experience.
        </div>
      ) : (
        <div className="mt-4 rounded-2xl border border-white/10 bg-black/40 p-4" data-testid="reveal-rounds">
        <div className="text-[9px] tracking-[0.3em] uppercase font-bold text-[#FF107A] mb-2">Round-by-round</div>
        {(rounds || []).map((r, i) => {
          const pair = pairings.find((p) => p.id === r.pairing_id) || {};
          const winnerName = r.chosen_side === "a" ? pair.a : pair.b;
          return (
            <div key={i} data-testid={`reveal-round-${i}`}
              className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-0 text-sm">
              <span className="text-[10px] tabular text-[#FDFBF7]/50 w-4">{i + 1}</span>
              {r.round_perfect && <Check size={12} className="text-[#22C55E] shrink-0" />}
              <span className="flex-1 truncate">
                <span className={r.celeb_correct ? "text-[#FDFBF7]" : "text-[#FDFBF7]/60"}>{winnerName}</span>
                <span className="text-[#FDFBF7]/40 text-xs"> · {r.chosen_reason}</span>
              </span>
              <span className="tabular font-display text-lg" style={{ color: r.round_perfect ? "#FFD700" : "#00E5FF" }}>
                +{r.points}
              </span>
            </div>
          );
        })}
      </div>
      )}

      {/* CSS/Streak/Rank */}
      <div className="mt-4 grid grid-cols-3 gap-2">
        <HeaderStat label="CSS" value={fmt(me?.css)} accent="#FFD700" testId="reveal-css" />
        <HeaderStat label="Streak" value={me?.daily_streak || 0} accent="#FF107A" testId="reveal-streak" />
        <HeaderStat label="Rank" value={me?.global_rank ? `#${me.global_rank}` : "—"} accent="#00E5FF" testId="reveal-rank" />
      </div>

      {/* Sharable card */}
      <div className="mt-6 flex justify-center">
        <SharableCard ref={cardRef} smashNumber={smash_number} factor={factor}
          score={score} archetype={archetype} rank={me?.global_rank} css={me?.css} />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <button data-testid="reveal-share-btn" onClick={share} disabled={busy}
          className="rounded-full bg-[#FF107A] text-[#FDFBF7] py-3 font-bold flex items-center justify-center gap-2 glow-pink disabled:opacity-60">
          <Download size={16} /> Save PNG
        </button>
        <button data-testid="reveal-leaderboard-btn" onClick={onLeaderboard}
          className="rounded-full border border-[#FFD700]/40 bg-[#FFD700]/10 text-[#FFD700] py-3 font-bold flex items-center justify-center gap-2">
          <Trophy size={16} /> Leaderboard
        </button>
      </div>

      <button data-testid="reveal-challenge-btn" onClick={challengeFriend}
        className="mt-3 w-full rounded-full py-3 font-bold text-[#0A090D] flex items-center justify-center gap-2 transition-all hover:scale-[1.01]"
        style={{
          background: "linear-gradient(135deg, #22C55E 0%, #34D399 50%, #4ADE80 100%)",
          border: "2px solid #86EFAC", boxShadow: "0 0 22px rgba(52,211,153,0.45)",
        }}>
        <Swords size={16} /> {challenge ? "Copy Link Again" : "Challenge a Friend"}
      </button>
      {challenge && (
        <div data-testid="reveal-challenge-link"
          className="mt-2 rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-[11px] text-[#FDFBF7]/70 break-all">
          {challenge.url}
        </div>
      )}

      {!user && (
        <button data-testid="reveal-signin-btn" onClick={onLogin}
          className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#FDFBF7] text-[#0A090D] px-6 py-3 font-bold tracking-wide">
          <LogIn size={16} /> Sign in to save your streak
        </button>
      )}

      <div className="mt-6 text-center text-[10px] tracking-[0.3em] uppercase text-[#FDFBF7]/50">
        Come back tomorrow · New Matchups
      </div>
    </motion.div>
  );
};

/* ---------- Sharable Card ---------- */
const SharableCard = React.forwardRef(({ smashNumber, factor, score, archetype, rank, css }, ref) => (
  <div ref={ref} data-testid="daily-share-card"
    className="rounded-[28px] p-5 flex flex-col relative overflow-hidden"
    style={{
      width: 320, height: 400,
      background: "radial-gradient(circle at 20% 10%, rgba(255,16,122,0.55), transparent 55%)," +
                  "radial-gradient(circle at 90% 100%, rgba(0,229,255,0.35), transparent 55%)," +
                  "linear-gradient(160deg, #16141D 0%, #0A090D 100%)",
      color: "#FDFBF7", fontFamily: "'Outfit', sans-serif",
    }}>
    <div className="flex items-center justify-between">
      <span className="text-[9px] tracking-[0.35em] uppercase font-bold text-[#FF107A]">Celebrity Smash</span>
      <span className="text-[9px] tracking-[0.2em] uppercase text-[#FDFBF7]/60">#{smashNumber}</span>
    </div>
    <div className="mt-1 font-display text-lg leading-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
      {factor.label}
    </div>
    <div className="mt-6 text-center">
      <div className="text-[9px] tracking-[0.3em] uppercase text-[#FDFBF7]/50">Score</div>
      <div className="font-display text-[72px] leading-[0.9] tabular text-[#FFD700]"
           style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{score.total}</div>
      <div className="text-[11px] tracking-[0.15em] uppercase text-[#FDFBF7]/70">
        {score.correct_celebs}/{score.rounds_total} matched · {score.perfect_rounds} perfect
      </div>
    </div>
    <div className="mt-auto grid grid-cols-2 gap-2">
      <div className="rounded-xl border border-white/10 bg-black/40 py-2 text-center">
        <div className="font-display text-lg tabular text-[#FF107A]">{css || 0}</div>
        <div className="text-[8px] tracking-[0.25em] uppercase text-[#FDFBF7]/60 mt-0.5">CSS</div>
      </div>
      <div className="rounded-xl border border-white/10 bg-black/40 py-2 text-center">
        <div className="font-display text-lg tabular text-[#FFD700]">{rank ? `#${rank}` : "—"}</div>
        <div className="text-[8px] tracking-[0.25em] uppercase text-[#FDFBF7]/60 mt-0.5">Rank</div>
      </div>
    </div>
    <div className="mt-3 text-center text-[10px] tracking-[0.3em] uppercase font-bold text-[#FFD700]">
      {archetype?.label || "Rookie"}
    </div>
  </div>
));
SharableCard.displayName = "SharableCard";

/* ---------- Shared ---------- */
const PrimaryBtn = ({ children, onClick, disabled, testId }) => (
  <button
    data-testid={testId}
    onClick={() => { if (disabled) { playError(); return; } playClick(); onClick && onClick(); }}
    disabled={disabled}
    className={`mt-6 w-full rounded-full py-3 font-bold flex items-center justify-center gap-2 transition-all ${
      disabled
        ? "bg-white/5 text-[#FDFBF7]/30 cursor-not-allowed"
        : "bg-[#FF107A] text-[#FDFBF7] glow-pink hover:scale-[1.01]"
    }`}
  >
    {children}
  </button>
);

const FullScreenMsg = ({ text }) => (
  <div className="min-h-screen spotlight grain scanlines pt-24 text-center text-[#FDFBF7]/60">{text}</div>
);
